import { useCallback, FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Typography, Button, Svg, useThemeValue } from '@altermaind/web/ui';
import { Routes } from '@/_shared/constants/routes';
import { convertSizeToNumber } from '@/_shared/helpers/convertSizeToNumber';
import { useDialog, useNotification } from '@/_shared/hooks';
import { useBreakingPoints } from '@/_shared/hooks/useBreakpoint';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import {
  getEmployeesImportsOverviewCall,
  getEmployeesImportsStatsCall,
  postEmployeesImportsAbortCall,
} from '../../state';

export const DialogPendingImportEmployees: FC = () => {
  const t = useTranslations('people.dialogPendingImportEmployees');
  const sharedT = useTranslations();
  const { genericResponsiveValue } = useResponsiveStyle();
  const { isDesktop } = useBreakingPoints();
  const navigate = useNavigate();
  const gapL = convertSizeToNumber(`${useThemeValue('sizes', 'l')}`);
  const { notify } = useNotification();
  const { openDialog, closeDialog, Dialog, isOpen } = useDialog('dialogPendingImportEmployees');
  const {
    execute: executeGetEmployeesImportsOverview,
    reset: resetGetEmployeesImportsOverview,
    pendingReviewImport,
  } = getEmployeesImportsOverviewCall.hook();
  const { execute: executePostEmployeesImportsAbort, loading: loadingPostEmployeesImportsAbort } =
    postEmployeesImportsAbortCall.hook();
  const { execute: executeGetEmployeesImportsStats, loading: loadingGetEmployeesImportsStats } =
    getEmployeesImportsStatsCall.hook();

  const onAbort = useCallback(() => {
    if (!pendingReviewImport?.pendingJob) {
      return;
    }
    executePostEmployeesImportsAbort({
      jobId: pendingReviewImport.pendingJob,
      successSaga: () => {
        notify(sharedT('success.genericApiSuccess'), 'success');
        resetGetEmployeesImportsOverview();
        closeDialog();
      },
      errorSaga: () => {
        notify(sharedT('errors.genericApiError'), 'error');
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeDialog, executePostEmployeesImportsAbort, pendingReviewImport?.pendingJob, sharedT]);

  const onConfirm = useCallback(() => {
    if (pendingReviewImport?.pendingJob) {
      executeGetEmployeesImportsStats({
        jobId: pendingReviewImport?.pendingJob,
        successSaga: () => {
          navigate(Routes.PeopleImport);
        },
        errorSaga: () => {
          notify(sharedT('errors.genericApiError'), 'error');
        },
      });
    }
  }, [pendingReviewImport?.pendingJob, executeGetEmployeesImportsStats, navigate, notify, sharedT]);

  useEffect(() => {
    if (pendingReviewImport) {
      openDialog();
    }
  }, [openDialog, pendingReviewImport]);

  useEffect(() => {
    executeGetEmployeesImportsOverview({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog
      open={isOpen}
      title={undefined}
      onClose={closeDialog}
      avoidCloseOnBackDrop
      closeButton={false}
      maxWidth='700px'
    >
      <Box display='flex' mx='l' flexDirection='column' px='s'>
        <Typography variant='h3' color='primary/text' textAlign='center'>
          {t('title')}
        </Typography>
      </Box>
      <Box display='flex' mt='l' flexDirection='column' px='s'>
        <Typography variant='p2/light' color='primary/text' textAlign='center'>
          {t('description')}
        </Typography>
      </Box>

      <Box
        display='flex'
        mx='l'
        flexDirection={genericResponsiveValue({
          breakPoints: { desktop: 'row' },
          defaultValue: 'column',
        })}
        px='s'
        style={{ gap: isDesktop ? gapL : 0 }}
        justifyContent='center'
      >
        <Button
          spinnerColor='white'
          loading={loadingPostEmployeesImportsAbort}
          variant='link/primary'
          onClick={onAbort}
          mt='l'
          style={{ textDecoration: 'none' }}
        >
          <Svg name='close' fill='primary/text' size='s' mr='xs' />
          {t('cancel')}
        </Button>

        <Button
          variant='primary'
          onClick={onConfirm}
          loading={loadingGetEmployeesImportsStats}
          spinnerColor='white'
          mt={genericResponsiveValue({
            breakPoints: { desktop: 'l' },
            defaultValue: 'xs',
          })}
        >
          {t('confirm')}
        </Button>
      </Box>
    </Dialog>
  );
};
